import {CdkPortalOutlet} from "@angular/cdk/portal";
import {
    Component,
    ComponentRef,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import {AbstractControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Subscription} from "rxjs";
import {FormControlBaseComponent} from "../../../../shared/components/form-controls/form-control-base/form-control-base.component";
import {SearchScopeEnum} from "../../../../shared/enums/search-scope.enum";
import {EmptySearchParamsFilterModel} from "../../../../shared/models/search/params/filters/empty-search-params-filter.model";
import {SearchResultsFilterModel} from "../../../../shared/models/search/results/filters/search-results-filter.model";
import {SearchResultItemModel} from "../../../../shared/models/search/results/items/search-result-item.model";
import {TabFactoryService} from "../../../services/factories/tab-factory.service";
import {SearchTabDirective} from "../../tabs/search-tab.directive";
import {ScopedSearchResultModel} from "../../../models/search-results/scoped-search-result.model";

@Component({
    selector: "pcg-ng-search-tab-proxy",
    template: require('./search-tab-proxy.component.html'),
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchTabProxyComponent), multi: true}
    ]
})
export class SearchTabProxyComponent extends FormControlBaseComponent<EmptySearchParamsFilterModel> implements OnChanges, OnDestroy {
    @Input()
    public scope: SearchScopeEnum;

    @Input()
    public searchResult: ScopedSearchResultModel<SearchResultItemModel, SearchResultsFilterModel>;

    @Input()
    public paginationControl: AbstractControl;

    @ViewChild(CdkPortalOutlet)
    public outlet: CdkPortalOutlet;

    public searchTab: ComponentRef<SearchTabDirective<EmptySearchParamsFilterModel, ScopedSearchResultModel<SearchResultItemModel, SearchResultsFilterModel>>>;

    private changeSubscription: Subscription;

    public constructor(
        private tabFactoryService: TabFactoryService
    ) {
        super(/*DEFAULT_VALUE*/{});
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const {searchResult} = changes;
        if (searchResult && searchResult.currentValue !== searchResult.previousValue) {
            this.setSearchResult(searchResult.currentValue);
        }
    }

    public ngOnDestroy(): void {
        this.unSubscribeChanges();
        if (this.outlet) {
            this.outlet.dispose();
        }
    }

    public createTab(): void  {
        this.cleanUp();
        this.mountTab();
        this.initialiseTab();
    }

    private mountTab(): void  {
        const portal = this.tabFactoryService.createTabPortal(this.scope);
        this.searchTab = this.outlet.attachComponentPortal(portal);
    }

    private initialiseTab(): void  {
        this.setSearchResult(this.searchResult);
        this.setSearchParamsFilter(this.controlValue);
        this.setPaginationControl(this.paginationControl);

        this.changeSubscription = this.searchTab.instance.filterSubmitted
            .subscribe((filter: EmptySearchParamsFilterModel) => {
                this.writeNewValue(filter);
            });
    }

    private setSearchParamsFilter(value: EmptySearchParamsFilterModel): void  {
        if (!this.searchTab) {
            return;
        }

        this.searchTab.instance.filter = value;
    }

    private setPaginationControl(value: AbstractControl): void {
        if (!this.searchTab) {
            return;
        }

        this.searchTab.instance.paginationControl = value;
    }

    private setSearchResult(searchResult: ScopedSearchResultModel<SearchResultItemModel, SearchResultsFilterModel>): void  {
        if (!this.searchTab) {
            return;
        }
        this.searchTab.instance.searchResult = searchResult;
    }

    private unSubscribeChanges(): void  {
        if (this.changeSubscription) {
            this.changeSubscription.unsubscribe();
            this.changeSubscription = null;
        }
    }

    private cleanUp(): void  {
        this.unSubscribeChanges();
        if (!this.outlet || !this.outlet.hasAttached()) {
            return;
        }

        this.outlet.detach();
        this.searchTab = null;
    }
}
